import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { UpdateSensorRequest } from "@opus-dt/shared-types";
import type { SensorSummary } from "../../types/sensors.types.js";

export type { SensorSummary } from "../../types/sensors.types.js";

const QK = ["sensors"] as const;

export function useSensors() {
  return useQuery<SensorSummary[]>({
    queryKey: QK,
    queryFn: () => api.get<SensorSummary[]>("/api/v1/sensors"),
  });
}

// Edição inline do sensor (apenas code/name/dataType; cardId/position/escala imutáveis).
// PATCH /sensors/:id retorna um shape estreito (não o SensorSummary completo) — tipamos só o que
// o serviço devolve. O resultado não é consumido; a UI reconcilia via invalidação.
type UpdatedSensor = { id: string; code: string; name: string; dataType: string };
export function useUpdateSensor(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSensorRequest }) =>
      api.patch<UpdatedSensor>(`/api/v1/sensors/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: ["cards", deviceId] });
      // Sensor vinculado: o código/nome aparece na sidebar da máquina (["lines",lineId,"machines"])
      // e em badges de parâmetro (["parameters",machineId]). Invalida por prefixo.
      void qc.invalidateQueries({ queryKey: ["lines"] });
      void qc.invalidateQueries({ queryKey: ["parameters"] });
    },
  });
}

// Criação de sensor migrou 100% para o detalhe do CLP
// (useCreateSensorPosition em features/cards). A tela de Sensores é consulta:
// lista + exclusão. Não há mais criação direta nem rotação de chave.
export function useDeleteSensor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/sensors/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
